/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import moment from 'moment'
import LegoPickerRaw from '@mdsol/lego/lib/DatePicker'
import { getDatePickerParseFormats } from '~/utils/dateFormatting'

const LegoPicker = LegoPickerRaw as React.ComponentType<any>

/**
 * DatePicker wrapper that uses @mdsol/lego/lib/DatePicker internally.
 * Accepts JS Date objects (react-datepicker-compatible API) and converts
 * them to/from moment objects required by lego DatePicker internally.
 *
 * Use this component instead of importing react-datepicker directly —
 * it ensures correct format-token handling (lego uses moment internally).
 * For date pickers that require time input (showTimeInput / showTimeSelect),
 * use react-datepicker directly with getDateFnsParseFormats().
 */
interface CustomDatePickerProps {
  selected?: Date | null
  onChange: (date: Date | null) => void
  minDate?: Date | null
  maxDate?: Date | null
  placeholderText?: string
  showYearDropdown?: boolean
  todayButton?: string
  dateFormatCalendar?: string
  popoverTargetOffset?: string
  popperPlacement?: string
  onBlur?: React.FocusEventHandler<HTMLElement>
  className?: string
  id?: string
  name?: string
  disabled?: boolean
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({ selected, onChange, minDate, maxDate, ...rest }) => {
  const toMoment = (d?: Date | null) => (d ? moment(d) : null)

  return (
    <LegoPicker
      selected={toMoment(selected)}
      onChange={(m: moment.Moment | null) => onChange(m ? m.toDate() : null)}
      minDate={toMoment(minDate)}
      maxDate={toMoment(maxDate)}
      dateFormat={getDatePickerParseFormats()}
      {...rest}
    />
  )
}

export default CustomDatePicker
